import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';

import { EmployeeItemInfoData } from '~/components/GenericList/EmployeeItemInfo';
import {
  OrgUnitEmployeeRole,
  OrganizationUnit,
  ResponsibleEmps,
} from '~/data/orgunits';
import {
  AlternateManagerRow,
  OrgUnitFormData,
  OrganizationUnitSubmission,
} from '~/scenes/PortalViews/Views/PortalAltMgr/components/OrgUnitPopup/types';

export const convertOrganizationUnitToFormData = (
  organizationUnit: OrganizationUnit,
  alternateMgrs: EmployeeItemInfoData[],
): OrgUnitFormData => {
  const defaultCostCenters = organizationUnit.fOrgUnitCostCenters || [];

  const alternateManagers: AlternateManagerRow[] =
    organizationUnit.fOrgUnitResponsibleEmps
      .filter((emp) => emp.fUnitRole === OrgUnitEmployeeRole.AlternateManager)
      .map(
        (emp): AlternateManagerRow => ({
          id: uuidv4(),
          entryId: emp.fId,
          manager:
            alternateMgrs.find((mgr) => mgr.employeeId === emp.fEmployeeId) ||
            null,
          startDate: emp.fStartDate || '',
          endDate: emp.fEndDate || '',
          savedWithoutDates: !emp.fStartDate && !emp.fEndDate,
        }),
      );

  const filterResponsibleEmps = (
    roles: OrgUnitEmployeeRole[],
  ): ResponsibleEmps[] =>
    organizationUnit.fOrgUnitResponsibleEmps.filter((emp) =>
      roles.includes(emp.fUnitRole),
    );

  // TODO sort responsibleHRs
  const responsibleHRs = filterResponsibleEmps([
    OrgUnitEmployeeRole.HRAdmin,
    OrgUnitEmployeeRole.HRStaff,
    OrgUnitEmployeeRole.HRDev,
    OrgUnitEmployeeRole.HRSalary,
    OrgUnitEmployeeRole.HRLimited,
    OrgUnitEmployeeRole.HRAuditor,
    OrgUnitEmployeeRole.HRPeople,
    OrgUnitEmployeeRole.HRPeopleDev,
  ]).sort((a, b) => {
    const getIndex = (emp: ResponsibleEmps) => {
      if (!!emp.fEmployeeId) {
        return organizationUnit.fOrgUnitResponsibleHrEmps.indexOf(
          emp.fEmployeeId,
        );
      } else {
        return -1;
      }
    };
    const indexA = getIndex(a);
    const indexB = getIndex(b);
    return indexA > indexB ? 1 : indexA === indexB ? 0 : -1;
  });

  const deputyManagers = filterResponsibleEmps([
    OrgUnitEmployeeRole.DeputyManager,
  ]);

  const substitute = organizationUnit.fOrgUnitResponsibleEmps.find(
    (emp) => emp.fUnitRole === OrgUnitEmployeeRole.SubstituteAbsenceManager,
  );

  return {
    fOrgUnitName: organizationUnit.fOrgUnitName,
    fOrgUnitNumber: organizationUnit.fOrgUnitNumber,
    fOrgUnitType: organizationUnit.fOrgUnitType,
    fOrgUnitLoc: organizationUnit.fOrgUnitLoc,
    fOrgUnitUrl: organizationUnit.fOrgUnitUrl,
    fOrgUnitCreated: moment(organizationUnit.fOrgUnitCreated).format(
      'YYYY-MM-DD',
    ),
    fOrgUnitAvail: moment(organizationUnit.fOrgUnitAvail).format('YYYY-MM-DD'),
    fOrgUnitManager: organizationUnit.fOrgUnitManager,
    fOrgUnitResponsibleEmps: organizationUnit.fOrgUnitResponsibleEmps || [],
    fOrgUnitContainingTrees: organizationUnit.fOrgUnitContainingTrees || [],
    fOrgUnitCostCenters: defaultCostCenters,
    substituteManager: substitute || null,
    substituteStartDate: substitute?.fStartDate || '',
    substituteEndDate: substitute?.fEndDate || '',
    alternateManagers,
    responsibleHRs,
    deputyManagers,
  };
};

export const convertFormDataToOrganizationUnit = (
  formData: OrgUnitFormData,
  editedUnit?: OrganizationUnit,
): OrganizationUnitSubmission => {
  const responsibleEmps_other: ResponsibleEmps[] =
    formData.fOrgUnitResponsibleEmps.filter(
      (emp) =>
        ![
          OrgUnitEmployeeRole.DeputyManager,
          OrgUnitEmployeeRole.HRAdmin,
          OrgUnitEmployeeRole.SubstituteAbsenceManager,
          OrgUnitEmployeeRole.AlternateManager,
          OrgUnitEmployeeRole.HRStaff,
          OrgUnitEmployeeRole.HRDev,
          OrgUnitEmployeeRole.HRSalary,
          OrgUnitEmployeeRole.HRLimited,
          OrgUnitEmployeeRole.HRAuditor,
          OrgUnitEmployeeRole.HRPeople,
          OrgUnitEmployeeRole.HRPeopleDev,
        ].includes(emp.fUnitRole),
    );

  const responsibleEmps_deputy: ResponsibleEmps[] = formData.deputyManagers;

  const responsibleEmps_hrAdmin: ResponsibleEmps[] = formData.responsibleHRs;

  const responsibleEmps_hrAdmin_order: number[] = formData.responsibleHRs
    .map((hr) => hr.fEmployeeId)
    .filter((hrId): hrId is number => typeof hrId === 'number');

  const responsibleEmps_substitute: ResponsibleEmps[] =
    formData.substituteManager
      ? [
          {
            ...formData.substituteManager,
            fStartDate: formData.substituteStartDate || undefined,
            fEndDate: formData.substituteEndDate || undefined,
          },
        ]
      : [];

  const responsibleEmps_altManagers = formData.alternateManagers
    .filter((row) => row.manager)
    .map((row) => ({
      fId: row.entryId,
      fEmployeeId: row.manager!.employeeId,
      fUnitRole: OrgUnitEmployeeRole.AlternateManager,
      fStartDate: row.startDate || undefined,
      fEndDate: row.endDate || undefined,
    }));

  const responsibleEmps: ResponsibleEmps[] = [
    ...responsibleEmps_other,
    ...responsibleEmps_deputy,
    ...responsibleEmps_hrAdmin,
    ...responsibleEmps_substitute,
    ...responsibleEmps_altManagers,
  ];

  const inheritedProps = editedUnit
    ? {
        fOrgUnitId: editedUnit.fOrgUnitId,
        fOrgUnitPos: editedUnit.fOrgUnitPos,
      }
    : {
        fOrgUnitPos: [], // TODO What should be here?
      };

  return {
    ...inheritedProps,
    fOrgUnitName: formData.fOrgUnitName,
    fOrgUnitNumber: formData.fOrgUnitNumber,
    fOrgUnitType: formData.fOrgUnitType,
    fOrgUnitLoc: formData.fOrgUnitLoc,
    fOrgUnitUrl: formData.fOrgUnitUrl,
    fOrgUnitCreated: formData.fOrgUnitCreated,
    fOrgUnitAvail: formData.fOrgUnitAvail,
    fOrgUnitManager: formData.fOrgUnitManager,
    fOrgUnitResponsibleEmps: responsibleEmps,
    fOrgUnitResponsibleHrEmps: responsibleEmps_hrAdmin_order,
    fOrgUnitContainingTrees: formData.fOrgUnitContainingTrees,
    fOrgUnitCostCenters: formData.fOrgUnitCostCenters,
  };
};
